import { useTranslations } from '@vocab/react';
import {
  Inline,
  Text,
  Badge,
  Link,
  Box,
  TooltipRenderer,
} from 'braid-design-system';
import type React from 'react';

import { JobStatus } from 'src/graphql/__generated__';
import {
  isBetweenJobStreetMigrationCutOff,
  isBetweenJobsDBMigrationCutOff,
} from 'src/utils/isBetweenMigrationCutOff';
import { isJobStreetSite, isJobsDBSite } from 'src/utils/isSite';
import { formatNumber } from 'src/utils/numbers';

import { useConfig } from '../../../hooks/useConfig';
import { EmptyTableCell } from '../EmptyTableCell';
import { createCandidateManagementLink } from '../JobActions/links';
import { Skeleton } from '../Skeleton/Skeleton';
import { TrackingLink } from '../TrackingLink/TrackingLink';

import translations from './.vocab';
interface Props {
  jobId: string;
  numberOfNewCandidates?: number;
  numberOfCandidates?: number;
  createdDate?: string;
  status?: JobStatus;
  loading?: boolean;
}

const shouldShowNAforNumCandidates = (
  location: string,
  createdDate?: string,
): boolean => {
  if (!createdDate) {
    return false;
  }

  if (isJobsDBSite(location)) {
    return isBetweenJobsDBMigrationCutOff(createdDate);
  } else if (isJobStreetSite(location)) {
    return isBetweenJobStreetMigrationCutOff(createdDate);
  }

  return false;
};

export const JobNumOfCandidates: React.FC<React.PropsWithChildren<Props>> = ({
  numberOfNewCandidates = 0,
  numberOfCandidates,
  createdDate = '',
  status,
  loading = false,
  jobId,
}) => {
  const { t } = useTranslations(translations);
  const { urlResolver, site } = useConfig();

  if (loading) {
    return (
      <Text>
        <Skeleton loading numberOfCharacters={5} />
      </Text>
    );
  }

  const jobTitleLinkUrl = urlResolver({
    path: createCandidateManagementLink(jobId),
  });

  if (status === JobStatus.ACTIVE || status === JobStatus.EXPIRED) {
    if (shouldShowNAforNumCandidates(site, createdDate)) {
      return (
        <Inline space="small" alignY="center">
          <TooltipRenderer
            id={`joblist-number-of-candidates-not-migrated${jobId}`}
            tooltip={
              <Box padding="small">
                <Text align="center" size="small">
                  {t('Applications are not available for this job ad')}
                </Text>
              </Box>
            }
            placement="bottom"
          >
            {({ triggerProps }) => (
              <Box {...triggerProps}>
                <Text size="small">{t('Not Applicable')}</Text>
              </Box>
            )}
          </TooltipRenderer>
        </Inline>
      );
    }

    return (
      <Inline space="small" alignY="center">
        <TrackingLink
          eventName="candidate_count_pressed"
          href={jobTitleLinkUrl}
          testId="numberOfCandidatesLink"
          weight="weak"
        >
          <Text size="small">
            {numberOfCandidates && formatNumber(numberOfCandidates)}
          </Text>
        </TrackingLink>
        {numberOfNewCandidates > 0 && (
          <Link data-testId="numberOfNewCandidatesLink" href={jobTitleLinkUrl}>
            <Badge tone="positive">
              {t('New', {
                numberOfNewCandidates,
              })}
            </Badge>
          </Link>
        )}
      </Inline>
    );
  }

  // draft job, pending job etc
  return <EmptyTableCell />;
};
